// =============================================================================
// qonto.ts — Helpers PURS pour l'intégration Qonto SEPA Direct Debit (SDD).
// =============================================================================
// Prélèvement automatique des clients (restos) par Lil & Co :
//   Client Qonto (débiteur) → mandat (email signature) → subscription (encaissement)
//   → suivi collection (webhooks). Doc : docs.qonto.com (SDD Collection API).
//
// Ce module est SANS effet de bord (testable par vitest). Les Edge Functions Deno
// réutilisent les mêmes URLs/scopes (re-déclarés côté fonction pour rester
// self-contained, comme les fonctions Odoo/Stripe existantes).
// =============================================================================

export type QontoEnv = 'sandbox' | 'production';

/** Scopes OAuth requis : SDD + identification du compte + gestion des clients débiteurs. */
export const QONTO_SCOPES = [
  'offline_access', // refresh token (intégration non interactive)
  'organization.read', // récupérer le bank_account_id créditeur
  'client.read',
  'client.write', // créer le client débiteur (resto)
  'sepa_direct_debit.read',
  'sepa_direct_debit.write',
] as const;

/** Bases d'URL Qonto selon l'environnement (cf. docs.qonto.com/get-started/business-api/urls). */
export function qontoUrls(env: QontoEnv): { oauth: string; api: string } {
  return env === 'production'
    ? { oauth: 'https://oauth.qonto.com', api: 'https://thirdparty.qonto.com' }
    : { oauth: 'https://oauth-sandbox.staging.qonto.co', api: 'https://thirdparty-sandbox.staging.qonto.co' };
}

/** Normalise la valeur du secret QONTO_ENV en environnement valide (défaut sandbox). */
export function parseQontoEnv(raw?: string | null): QontoEnv {
  return (raw || '').toLowerCase() === 'production' ? 'production' : 'sandbox';
}

/** Construit l'URL d'autorisation OAuth (redirection du super admin pour connecter le compte). */
export function buildAuthorizeUrl(p: {
  oauthBase: string;
  clientId: string;
  redirectUri: string;
  scopes: readonly string[];
  state: string;
  organizationId?: string;
}): string {
  const params = new URLSearchParams({
    client_id: p.clientId,
    redirect_uri: p.redirectUri,
    response_type: 'code',
    scope: p.scopes.join(' '),
    state: p.state,
  });
  if (p.organizationId) params.set('organization_id', p.organizationId);
  return `${p.oauthBase}/oauth2/auth?${params.toString()}`;
}

/** Convertit des centimes en montant Qonto { value: "2.97", currency }. */
export function centsToQontoAmount(cents: number, currency: 'EUR' | 'CHF' = 'EUR'): { value: string; currency: string } {
  const v = Math.round(cents) / 100;
  return { value: v.toFixed(2), currency };
}

/** Inverse : "2.97" → 297 centimes (réconciliation collection ↔ facture). */
export function qontoAmountToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

/**
 * Date d'encaissement au format YYYY-MM-DD = base + `offsetDays` (défaut +2).
 * Le SDD CORE exige un préavis ; +2 jours est un défaut prudent (ajustable).
 */
export function collectionDateISO(base: Date, offsetDays = 2): string {
  const d = new Date(base.getTime());
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/** Statut mandat Qonto → état interne simple. */
export type MandateState = 'none' | 'pending_signature' | 'approved' | 'unknown';
export function normalizeMandateStatus(status?: string | null): MandateState {
  if (!status) return 'none';
  if (status === 'pending_signature') return 'pending_signature';
  if (status === 'approved') return 'approved';
  return 'unknown';
}

/** Statut d'encaissement Qonto → issue métier (cf. doc collection statuses). */
export type CollectionOutcome = 'pending' | 'settled' | 'failed' | 'reversed';
export function collectionOutcome(status?: string | null): CollectionOutcome {
  switch (status) {
    case 'completed':
      return 'settled';
    case 'declined':
    case 'rejected':
    case 'canceled':
      return 'failed';
    case 'returned':
    case 'refunded':
      return 'reversed';
    default:
      return 'pending';
  }
}

/** Un encaissement n'est déclenchable que si le mandat est signé (approved). */
export function canCollect(mandateStatus?: string | null): boolean {
  return normalizeMandateStatus(mandateStatus) === 'approved';
}

/** Libellé court FR de l'état mandat (affichage super admin). */
export function mandateLabel(status?: string | null): string {
  switch (normalizeMandateStatus(status)) {
    case 'approved':
      return 'Mandat signé';
    case 'pending_signature':
      return 'En attente de signature';
    case 'none':
      return 'Aucun mandat';
    default:
      return 'Statut inconnu';
  }
}
